import {parse} from 'graphql';
import {print as astToString} from 'query-api-graphql';
import {
  FieldNode,
  InlineFragmentNode,
  Kind,
  OperationDefinitionNode,
  SelectionNode,
  SelectionSetNode,
} from 'query-api-graphql';

import {
  FormState,
  InitializedFormState,
  RelatedResultsFormState,
} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import {assert} from 'Asserts';
import {sum} from 'BenchlingUtil';
import {getQueryDefinition} from 'graphql-client/AST';
import notifications from 'notifications';
import buildQueryApiQueryAst from 'pipelines/query-api-step-config/buildQueryApiQueryAst';
import {MAX_NUM_QUERY_API_FIELDS} from 'pipelines/query-api-step-config/SchemaDatasetCreationConstants';
import countSelectedFields from 'query-api/graphql-field-selector/countSelectedFields';
import {buildAnyOfFilterValue} from 'query-api/QueryApiFilters';
import {QueryApiIntrospection} from 'query-api/useQueryApiIntrospection';

export const DEFAULT_QUERY_FORM_STATE = {entityListingFieldName: null};
export const EMPTY_FIELD_SELECTIONS = {
  kind: Kind.SELECTION_SET,
  // TODO: Select default fields for the schema according to lookupBy
  selections: [],
} as SelectionSetNode;

/**
 * Builds a GraphQL query string from the given form state.
 * This function is the reverse operation of `buildInitialQueryFormStateFromQueryString`.
 *
 * @param formState - The form state containing entity listing field name, filters, and field selections
 * @returns A GraphQL query string representation, or null if the query AST cannot be built
 */
export function buildQueryStringFromFormState(formState: FormState): string | null {
  const queryAst = buildQueryApiQueryAst(formState);
  return queryAst != null ? astToString(queryAst) : null;
}

export interface BuildInitialQueryFormStateResult {
  formState: FormState;
  errorMessages?: Array<string>;
}

/**
 * Builds an initial query form state from a schema ID and query string.
 * This function is the reverse operation of `buildQueryStringFromFormState`.
 * Does not set filters in the form state.
 *
 * @param maybeSchemaId - The schema ID to build the form state for, or null
 * @param maybeQueryString - The GraphQL query string to parse, or null
 * @param queryApiIntrospection - The Query API introspection data
 * @returns An object containing the FormState and optional error messages
 */
export function buildInitialQueryFormStateFromQueryString(
  maybeSchemaId: string | null,
  maybeQueryString: string | null,
  queryApiIntrospection: QueryApiIntrospection
): BuildInitialQueryFormStateResult {
  if (maybeSchemaId == null) {
    return {formState: DEFAULT_QUERY_FORM_STATE};
  }

  const listingField = queryApiIntrospection.getQueryListingFieldForSchema(maybeSchemaId);
  if (listingField == null) {
    return {
      formState: DEFAULT_QUERY_FORM_STATE,
      errorMessages: [
        `The selected schema (${maybeSchemaId}) does not exist or you do not have access to it. Please select another schema.`,
      ],
    };
  }

  const {entityFieldSelections, relatedResultsFormStates} = parseQueryStringToFields(maybeQueryString);

  return {
    formState: {
      entityListingFieldName: listingField.name,
      entityFilterArguments: [
        {
          kind: Kind.ARGUMENT,
          name: {
            kind: Kind.NAME,
            value: 'id',
          },
          value: buildAnyOfFilterValue([]),
        },
      ],
      entityFieldSelections,
      relatedResultsFormStates,
    },
  };
}

/**
 * Returns true if the given field selection is a related results backlink field,
 * identified by the presence of a `joinKey` argument.
 */
function isBacklinkFieldSelection(selection: SelectionNode): selection is FieldNode {
  return (
    selection.kind === Kind.FIELD &&
    selection.arguments != null &&
    selection.arguments.some((arg) => arg.name.value === 'joinKey')
  );
}

/**
 * Converts a backlink FieldNode (with joinKey argument) into a RelatedResultsFormState.
 */
function parseBacklinkFieldToFormState(field: FieldNode): RelatedResultsFormState {
  const joinKeyArg = field.arguments?.find((arg) => arg.name.value === 'joinKey');
  const joinKeyFieldName =
    joinKeyArg != null && joinKeyArg.value.kind === Kind.STRING ? joinKeyArg.value.value : null;

  const hasFirstArg = field.arguments?.some((arg) => arg.name.value === 'first') ?? false;

  return {
    backlinkFieldName: field.name.value,
    backlinkSelections: field.selectionSet
      ? buildFormattedSelectionSet(field.selectionSet)
      : EMPTY_FIELD_SELECTIONS,
    joinKeyFieldName,
    onlyIncludeLatestItem: hasFirstArg,
  };
}

/**
 * Parses a GraphQL query string and extracts both entity field selections and related results
 * (backlink) field configurations.
 *
 * The parsing makes several assumptions about the structure and content of the input query:
 *
 * 1. **Specific query shape requiring unwrapping**: The queries we parse will be of the following
 *    shape that needs to be unwrapped before we can parse the field selections. The outer structure
 *    is a query operation with one root field selection (e.g., `plasmidItems(id: {anyOf: []}) { ... }`).
 *    We need to extract the SelectionSet inside that root field to get the actual field selections
 *    we want to preserve.
 *
 * 2. **No FragmentSpreadNode expected**: We don't expect to see FragmentSpreadNode in the queries
 *    built for data frame creation from schema field selection. This same assumption is made in
 *    the data frame creation util `buildSelectionString` and the query api util `countSelectedFields`.
 *
 * 3. **Inline Fragment nodes always have typeCondition**: The way we generate queries for data frame
 *    creation via schema field selection (vs for directive application) means that Inline Fragment
 *    nodes will always have a typeCondition.
 *
 * 4. **Backlink fields have a `joinKey` argument**: Fields representing related results are
 *    identified by the presence of a `joinKey` argument, and are separated into
 *    `relatedResultsFormStates` rather than being included in `entityFieldSelections`.
 *
 * @param queryString - The GraphQL query string to parse, or null
 * @returns Entity field selections and related results form states parsed from the query
 */
function parseQueryStringToFields(
  queryString: string | null
): Pick<InitializedFormState, 'entityFieldSelections' | 'relatedResultsFormStates'> {
  if (queryString == null) {
    return {
      entityFieldSelections: EMPTY_FIELD_SELECTIONS,
      relatedResultsFormStates: [],
    };
  }

  const ast = parse(queryString);
  // Parse the query string given its expected shape like plasmidItems(id: {anyOf: []}) { ... }
  const queryDefinition = getQueryDefinition(ast) as OperationDefinitionNode;
  const firstSelection = queryDefinition?.selectionSet?.selections?.[0];

  if (firstSelection && firstSelection.kind !== Kind.FRAGMENT_SPREAD && firstSelection.selectionSet) {
    const entitySelections: Array<FieldNode | InlineFragmentNode> = [];
    const relatedResultsFormStates: Array<RelatedResultsFormState> = [];

    for (const selection of firstSelection.selectionSet.selections) {
      if (isBacklinkFieldSelection(selection)) {
        relatedResultsFormStates.push(parseBacklinkFieldToFormState(selection));
      } else {
        entitySelections.push(buildFormattedNode(selection));
      }
    }

    return {
      entityFieldSelections: {
        kind: Kind.SELECTION_SET,
        selections: entitySelections,
      },
      relatedResultsFormStates,
    };
  }

  // We should never get here, because all persisted queries should be of the expected shape,
  // and even if previously selected fields no longer exist (ex: if you change the entity type),
  // that doesn't impact our ability to parse the query string.
  assert(false, 'Previously selected fields could not be resolved.', {queryString});
}

function buildFormattedSelectionSet(node: SelectionSetNode): SelectionSetNode {
  return {
    kind: Kind.SELECTION_SET,
    selections: node.selections.map((selection) => buildFormattedNode(selection)),
  };
}

function buildFormattedNode(node: SelectionNode): FieldNode | InlineFragmentNode {
  const {kind} = node;
  switch (kind) {
    case Kind.FIELD: {
      const {selectionSet} = node;
      return {
        kind,
        name: {kind: Kind.NAME, value: node.name.value},
        ...(selectionSet && {
          selectionSet: buildFormattedSelectionSet(selectionSet),
        }),
      };
    }

    case Kind.INLINE_FRAGMENT: {
      const {selectionSet, typeCondition} = node;
      return {
        kind,
        typeCondition: typeCondition && {
          kind: Kind.NAMED_TYPE,
          name: {kind: Kind.NAME, value: typeCondition.name.value},
        },
        ...(selectionSet && {
          selectionSet: buildFormattedSelectionSet(selectionSet),
        }),
      };
    }

    default: {
      // We don't expect to see any other selection kinds in the queries we store for LookUp steps
      throw new Error(`Unhandled selection kind: ${kind}`);
    }
  }
}

/**
 * Checks if the total number of selected fields in a query form state exceeds the maximum allowed.
 * Shows an error notification if the limit is exceeded.
 *
 * @returns true if the field count exceeds the limit, false otherwise
 */
export function notifyIfFieldCountExceeded(formState: FormState): boolean {
  const totalFields =
    formState.entityListingFieldName == null
      ? 0
      : countSelectedFields(formState.entityFieldSelections.selections) +
        sum(
          formState.relatedResultsFormStates.map((resultFormState) =>
            countSelectedFields(resultFormState.backlinkSelections.selections)
          )
        );

  if (totalFields > MAX_NUM_QUERY_API_FIELDS) {
    notifications.error(`Only ${MAX_NUM_QUERY_API_FIELDS} column selections are allowed.`, {
      duration: 4,
    });
    return true;
  }

  return false;
}
